/**
 * 공개 랜딩 `/` 의 **A/B 판 나누기** — 순수 함수. middleware(Edge)가 판을 고르고, 브라우저 활동 기록이 쿠키를 읽는다.
 * 판 `a` = 질문 검색칸 · 「오늘의 뷰티 트렌드」 랜딩(`FindLandingClassicView`) · 판 `b` = 「회의에서 먼저 말하는 사람」 랜딩(`FindLandingView`).
 * 🔑 서버 env `LANDING_AB`(`ab` | `a` | `b` · 없거나 모르는 값이면 `ab`)가 한 판으로 고정하면 실험이 끝난다 — 이때 남은 쿠키는 지운다.
 * 🔑 `/?lp=a` · `/?lp=b` 는 그 판을 **보기만** 한다 — 쿠키를 건드리지 않고, 그 방문은 실험 기록에서 빠진다.
 */

/** 랜딩 판 */
export type LandingVariant = 'a' | 'b';

/** 나누는 방식 — `ab` 반반 · `a`/`b` 그 판으로 고정 */
export type LandingAbMode = 'ab' | LandingVariant;

/** 쿠키에 무엇을 할지 — `set` 새로 적는다 · `clear` 지운다 · `none` 그대로 */
export type LandingCookieAction = 'set' | 'clear' | 'none';

/** 판을 기억하는 퍼스트 파티 쿠키 — 브라우저 활동 기록이 읽으므로 httpOnly 가 아니다 */
export const LANDING_AB_COOKIE = 'cm_lp';

/** 쿠키 수명(초) — 90일 · 처음 나눈 날부터(다시 와도 늘리지 않는다) */
export const LANDING_AB_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

/** 판을 골라 보는 질의 이름 — `/?lp=a` · `/?lp=b` */
export const LANDING_PREVIEW_PARAM = 'lp';

/** 활동 기록 봉투의 `experiment_id` */
export const LANDING_EXPERIMENT_ID = 'landing-v2';

/** 판마다 middleware 가 `/` 를 옮겨 그리는 내부 주소 — canonical 은 둘 다 `/` */
export const LANDING_VARIANT_PATHS: Record<LandingVariant, string> = { a: '/lp/a', b: '/lp/b' };

/**
 * `/` 요청 하나의 판을 고른다.
 * 차례: 질의 `lp` 로 고른 판(쿠키 그대로) → env 고정(남은 쿠키는 `clear`) → 크롤러는 늘 `a` · 쿠키 없음 →
 * 쿠키에 판이 있으면 그 판 → 처음이면 `random() < 0.5` 이 `a`, 아니면 `b` 로 `set`.
 * @param input.preview 질의 `lp` 값 — `a` · `b` 면 그 판을 보여 주기만 한다(모르는 값은 없는 것으로 본다)
 * @param input.cookie `cm_lp` 쿠키 값(없으면 `undefined`)
 * @param input.userAgent 요청의 User-Agent
 * @param input.mode `parseLandingAbMode(process.env.LANDING_AB)`
 * @param input.random `[0, 1)` 난수 — 테스트가 주입한다
 * @returns 보일 판과 쿠키에 할 일
 */
export function assignLandingVariant(input: {
  preview?: string | null;
  cookie: string | null | undefined;
  userAgent: string | null | undefined;
  mode: LandingAbMode;
  random: () => number;
}): { variant: LandingVariant; cookie: LandingCookieAction } {
  const previewed = parseLandingVariant(input.preview);
  if (previewed) return { variant: previewed, cookie: 'none' };
  const remembered = parseLandingVariant(input.cookie);
  if (input.mode !== 'ab') return { variant: input.mode, cookie: input.cookie != null ? 'clear' : 'none' };
  if (isCrawler(input.userAgent)) return { variant: 'a', cookie: 'none' };
  if (remembered) return { variant: remembered, cookie: 'none' };
  return { variant: input.random() < 0.5 ? 'a' : 'b', cookie: 'set' };
}

/** @returns env 값 → 나누는 방식(`a` · `b` 가 아니면 `ab`) */
export function parseLandingAbMode(raw: string | null | undefined): LandingAbMode {
  const value = raw?.trim().toLowerCase();
  return value === 'a' || value === 'b' ? value : 'ab';
}

/** @returns 쿠키 값 → 판(모르는 값은 `null`) */
export function parseLandingVariant(raw: string | null | undefined): LandingVariant | null {
  return raw === 'a' || raw === 'b' ? raw : null;
}

/**
 * `document.cookie` 같은 쿠키 줄에서 판을 읽는다.
 * @returns 판 — 쿠키가 없거나 모르는 값이면 `null`
 */
export function readLandingVariant(cookieHeader: string | null | undefined): LandingVariant | null {
  if (!cookieHeader) return null;
  const pair = cookieHeader
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${LANDING_AB_COOKIE}=`));
  return parseLandingVariant(pair?.slice(LANDING_AB_COOKIE.length + 1));
}

/**
 * 공개 랜딩 `/` 를 질의 `lp` 로 골라 보고 있는지.
 * @param pathname 지금 경로
 * @param search 지금 질의 줄(`?lp=b` 꼴 · 앞의 `?` 는 있어도 없어도 된다)
 * @returns 고른 판 — `/` 가 아니거나 `lp` 가 `a` · `b` 가 아니면 `null`
 */
export function readLandingPreview(pathname: string | null | undefined, search: string | null | undefined): LandingVariant | null {
  if (pathname !== '/' || !search) return null;
  return parseLandingVariant(new URLSearchParams(search).get(LANDING_PREVIEW_PARAM));
}

/** @returns 흔한 검색·미리보기 크롤러의 User-Agent 인가 — 비었으면 사람으로 본다 */
export function isCrawler(userAgent: string | null | undefined): boolean {
  return Boolean(userAgent && CRAWLER_UA.test(userAgent));
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 검색엔진 · 링크 미리보기 · SEO 도구 · 헤드리스 점검기 — 앱 안 브라우저(네이버 · 다음 · 카카오톡 · 핀터레스트 앱)의 이름과 겹치지 않게 크롤러 이름만 적는다 */
const CRAWLER_UA =
  /[a-z]bot\/|bot;|\+https?:\/\/|crawler|spider|slurp|google-inspectiontool|mediapartners-google|bingpreview|yeti\/|daumoa|kakaotalk-scrap|facebookexternalhit|facebookcatalog|meta-externalagent|pinterestbot|whatsapp\/|quora link preview|embedly|chatgpt-user|anthropic-ai|claude-web|chrome-lighthouse|pagespeed|headlesschrome/i;
